import { connect } from 'react-redux';

import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { KnmInnField } from 'KnmPackage/MainModule/components/KnmInnField';
import { KnmOgrnField } from 'KnmPackage/MainModule/components/KnmOgrnField';
import { getTargetEntitySelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { parsePassport } from 'CorePackage/MainModule/common/parsePassport';
import { IInspectionResult } from 'APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ICountry } from 'APIPackage/CatalogsModule/interfaces/ICountry';
import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { ErknmFieldMark } from 'KnmErknmPackage/MainModule/components/ErknmFieldMark';
import { KnmSnilsField } from 'KnmPackage/MainModule/components/KnmSnilsField';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import {
  getOrganizationKppLabelBlockId,
  getOrganizationRafpNzaLabelBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';

import styles from './PmSubjectInfo.module.scss';

interface IErknmSubjectAddProps {
  id: string;
  name: string | undefined;
  inn: string | undefined;
  innLabelBlockId: string;
  ogrn: string | undefined;
  ogrnLabelBlockId: string;
  organizationNameBlockId: string;
  addressBlockId: string;
  checkSubjectResult: IKnmCheckSubjectResult | null;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  snils: string | undefined;
  ervkId: string | undefined;
  inspectionResults?: IInspectionResult[];
}

interface IErknmSubjectAddStoreProps {
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
}

const ErknmSubjectAddWithoutStore = ({
  id,
  name,
  inn,
  innLabelBlockId,
  ogrn,
  ogrnLabelBlockId,
  organizationNameBlockId,
  addressBlockId,
  checkSubjectResult,
  subjectType,
  snils,
  ervkId,
  subjectTypeOptions,
  inspectionResults,
}: IErknmSubjectAddProps & IErknmSubjectAddStoreProps) => {
  const isFIZ = subjectType?.label === KnmSubjectTypesEnum.FIZ;
  const isIFIZ = subjectType?.label === KnmSubjectTypesEnum.IFIZ;

  const ogrnFieldName = `${name}.ogrn`;
  const innFieldName = `${name}.inn`;
  const organizationNameFieldName = `${name}.organizationName`;
  const subjectTypeFieldName = `${name}.subjectTypePm`;
  const kppFieldName = `${name}.kpp`;
  const rafpNzaFieldName = `${name}.rafpNza`;
  const countryFieldName = `${name}.country`;
  const locationAddressFieldName = `${name}.locationAddress`;
  const workAddressFieldName = `${name}.workAddress`;
  const factualAddressFieldName = `${name}.factualAddress`;
  const snilsFieldName = `${name}.snils`;
  const documentFieldName = `${name}.document`;

  const normalizeFio = (value: string | undefined) => {
    if (!value) return value;

    return value.replace(/[^а-яёa-z-`' ]/gi, '');
  };

  const innOrSnilsWarning = isFIZ && !inn && !snils ? 'Необходимо заполнить либо ИНН, либо СНИЛС' : undefined;
  const filterUlCountries = (values: ICountry[]) => values?.filter(item => item.ulEnabled);
  const filterFlCountries = (values: ICountry[]) => values?.filter(item => item.flEnabled);

  const IROrganizationName = inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_NAME,
  );
  const IRLocationAddress = inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_ADDRESS,
  );
  const IRFactualAddress = inspectionResults?.find(
    i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_FACTUAL_ADDRESS,
  );
  const IRRafpNza = inspectionResults?.find(i => i.inspectedField === InspectionResultFieldEnum.RAFP_REGISTRY);

  return (
    <Row>
      <Row>
        <FieldBlock
          label={
            <>
              Тип контролируемого лица
              <ErknmFieldMark fieldName={subjectTypeFieldName} />
            </>
          }
          placementLabel="left"
          color="black"
          className={styles.FieldBlock}
        >
          <SelectInputField
            id={subjectTypeFieldName}
            name={subjectTypeFieldName}
            options={subjectTypeOptions}
            disabled={true}
            size="large"
          />
        </FieldBlock>
      </Row>

      {subjectType && (
        <>
          {((subjectType.label !== KnmSubjectTypesEnum.IUR && !isIFIZ) ||
            subjectType.label === KnmSubjectTypesEnum.FIUR ||
            isFIZ) && (
            <Row>
              {subjectType.label !== KnmSubjectTypesEnum.IUR && !isIFIZ && (
                <FieldBlock
                  label={
                    <>
                      Идентификационный номер налогоплательщика (ИНН)
                      <ErknmFieldMark fieldName={innFieldName} />
                    </>
                  }
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  id={innLabelBlockId}
                >
                  <KnmInnField
                    name={innFieldName}
                    disabled={true}
                    inn={inn}
                    isFiz={isFIZ}
                    isShowAutoCompleteField={
                      subjectType.label !== KnmSubjectTypesEnum.IUR && subjectType.label !== KnmSubjectTypesEnum.FIUR
                    }
                    ervkId={ervkId}
                    subjectType={subjectType}
                    checkSubjectResult={checkSubjectResult}
                    isShowAdditionalMessages={true}
                  />
                </FieldBlock>
              )}
              {subjectType.label === KnmSubjectTypesEnum.FIUR && (
                <FieldBlock
                  label={
                    <>
                      Номер записи об аккредитации в РАФП
                      <ErknmFieldMark fieldName={rafpNzaFieldName} />
                    </>
                  }
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  id={getOrganizationRafpNzaLabelBlockId(id)}
                >
                  <div className={styles.ColumnWrapper}>
                    <TextInputField name={rafpNzaFieldName} disabled={true} inspectionResult={IRRafpNza} />
                  </div>
                </FieldBlock>
              )}
              {isFIZ && (
                <FieldBlock
                  label={
                    <>
                      Страховой номер индивидуального лицевого счета (СНИЛС)
                      <ErknmFieldMark fieldName={snilsFieldName} />
                    </>
                  }
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                >
                  <KnmSnilsField name={snilsFieldName} warning={innOrSnilsWarning} disabled={true} />
                </FieldBlock>
              )}
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.UR || subjectType.label === KnmSubjectTypesEnum.IP) && (
            <Row>
              <FieldBlock
                label="Основной государственный регистрационный номер (ОГРН)"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
                id={ogrnLabelBlockId}
              >
                <KnmOgrnField name={ogrnFieldName} disabled={true} ogrn={ogrn} />
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.FIUR && (
            <Row>
              <FieldBlock
                label={
                  <>
                    Код причины постановки на учет (КПП)
                    <ErknmFieldMark fieldName={kppFieldName} />
                  </>
                }
                id={getOrganizationKppLabelBlockId(id)}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={kppFieldName} disabled={true} />
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.UR ||
            subjectType.label === KnmSubjectTypesEnum.IP ||
            subjectType.label === KnmSubjectTypesEnum.FIUR) && (
            <Row>
              <FieldBlock
                label={
                  <>
                    Наименование проверяемого лица
                    <ErknmFieldMark fieldName={organizationNameFieldName} />
                  </>
                }
                id={organizationNameBlockId}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextareaField
                  id={organizationNameBlockId}
                  inputClassName={styles.OrganizationNameInput}
                  name={organizationNameFieldName}
                  resize="none"
                  disabled={true}
                  inspectionResult={IROrganizationName}
                />
              </FieldBlock>
            </Row>
          )}
          {(isIFIZ || subjectType.label === KnmSubjectTypesEnum.FIZ) && (
            <Row>
              <FieldBlock
                id={organizationNameBlockId}
                label={
                  <>
                    Фамилия, имя, отчество (при наличии)
                    <ErknmFieldMark fieldName={organizationNameFieldName} />
                  </>
                }
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField
                  name={organizationNameFieldName}
                  disabled={true}
                  id={organizationNameBlockId}
                  parse={normalizeFio}
                />
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.UR || subjectType.label === KnmSubjectTypesEnum.FIUR) && (
            <Row>
              <FieldBlock
                id={addressBlockId}
                label="Адрес места нахождения"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={locationAddressFieldName} disabled={true} inspectionResult={IRLocationAddress} />
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.UR && (
            <Row>
              <FieldBlock
                label="Место нахождения (осуществления деятельности) контролируемого лица"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={workAddressFieldName} disabled={true} />
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.IP && (
            <Row>
              <FieldBlock
                label="Место фактического осуществления деятельности"
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={factualAddressFieldName} disabled={true} inspectionResult={IRFactualAddress} />
              </FieldBlock>
            </Row>
          )}
          {isIFIZ && (
            <Row>
              <FieldBlock
                label={
                  <>
                    Сведения о документе, удостоверяющем личность
                    <ErknmFieldMark fieldName={documentFieldName} />
                  </>
                }
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={documentFieldName} parse={parsePassport} disabled={true} />
              </FieldBlock>
            </Row>
          )}
          {subjectType.label === KnmSubjectTypesEnum.IUR && (
            <Row>
              <FieldBlock
                label={
                  <>
                    Наименование
                    <ErknmFieldMark fieldName={organizationNameFieldName} />
                  </>
                }
                id={organizationNameBlockId}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField id={organizationNameBlockId} name={organizationNameFieldName} disabled={true} />
              </FieldBlock>
            </Row>
          )}
          {(subjectType.label === KnmSubjectTypesEnum.IUR || isIFIZ) && (
            <Row>
              <FieldBlock
                label={
                  <>
                    {subjectType.label === KnmSubjectTypesEnum.IUR
                      ? 'Государство, в котором указанное юридическое лицо зарегистрировано'
                      : 'Гражданство (при наличии)'}
                    <ErknmFieldMark fieldName={countryFieldName} />
                  </>
                }
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <SelectInputFieldAutoOptions
                  id={countryFieldName}
                  name={countryFieldName}
                  isClearable={false}
                  size="medium"
                  optionName={CommonCatalogsNamesEnum.COUNTRIES}
                  disabled={true}
                  transformFunc={isIFIZ ? filterFlCountries : filterUlCountries}
                />
              </FieldBlock>
            </Row>
          )}
        </>
      )}
    </Row>
  );
};

const mapStateToProps = (state: IRootState): IErknmSubjectAddStoreProps => ({
  subjectTypeOptions: getTargetEntitySelectOptions(state),
});

export const ErknmSubjectAdd = connect(mapStateToProps)(ErknmSubjectAddWithoutStore);
